import { useEffect, useState, useRef, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { useAppData } from "../context/AppDataContext";
import { useAuth } from "../context/AuthContext";
import { TranslateButton } from "./TranslateButton";
import { MessageVoiceRecorder } from "./MessageVoiceRecorder";
import { toast } from "sonner";
import { Plus, Loader2, Mic, Square, Image as ImageIcon, Send, CalendarPlus, MessageSquare, Bell, GraduationCap, Eye, Video, Check, Clock, Trash2, Phone, Mail, ShieldCheck, KeyRound, User } from "lucide-react";
import { VideoEmbed } from "./VideoEmbed";
import { registerTrainingViews } from "../lib/trainingViews";
import { TrainingRsvp } from "./TrainingRsvp";
import { fmtStamp } from "../lib/datetime";
import { PasswordInput } from "./PasswordInput";
import * as lamejs from "@breezystack/lamejs";

const inp = "w-full border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");

function fmtReqDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

async function uploadFile(file, visibility) {
  const fd = new FormData();
  fd.append("file", file);
  if (visibility) fd.append("visibility", visibility);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

export function PatientRequests({ onCreated }) {
  const [specs, setSpecs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [form, setForm] = useState({ appt_type: "", preferred_specialist_id: "", reason: "", image: "", voice_note: "" });
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [encoding, setEncoding] = useState(false);
  const [noteUrl, setNoteUrl] = useState("");
  const [noteDur, setNoteDur] = useState(0);
  const [trim, setTrim] = useState({ start: 0, end: 0 });
  const audioCtxRef = useRef(null);
  const procRef = useRef(null);
  const streamRef = useRef(null);
  const pcmRef = useRef([]);
  const srRef = useRef(44100);
  const samplesRef = useRef(null);
  const blobRef = useRef(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([api.get("/specialists"), api.get("/me/appointment-requests")]);
      setSpecs(s.data); setReqs(r.data);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const deleteReq = async (r) => {
    if (!window.confirm("Delete this appointment request? You can submit a new one anytime.")) return;
    try { await api.delete(`/me/appointment-requests/${r.id}`); toast.success("Request deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const onImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const url = await uploadFile(f, "private"); setForm((p) => ({ ...p, image: url })); toast.success("Image attached"); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const cleanupAudio = () => {
    try { procRef.current && procRef.current.disconnect(); } catch (e) { console.debug("audio disconnect noop:", e); }
    try { audioCtxRef.current && audioCtxRef.current.close(); } catch (e) { console.debug("audio ctx close noop:", e); }
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    clearInterval(timerRef.current);
    procRef.current = null; audioCtxRef.current = null; streamRef.current = null;
  };

  const encodeMp3 = (float32, sr) => {
    const enc = new lamejs.Mp3Encoder(1, sr, 128);
    const chunks = []; const block = 1152;
    for (let i = 0; i < float32.length; i += block) {
      const slice = float32.subarray(i, i + block);
      const pcm = new Int16Array(slice.length);
      for (let j = 0; j < slice.length; j++) { const s = Math.max(-1, Math.min(1, slice[j])); pcm[j] = s < 0 ? s * 0x8000 : s * 0x7FFF; }
      const buf = enc.encodeBuffer(pcm);
      if (buf.length) chunks.push(new Int8Array(buf));
    }
    const end = enc.flush();
    if (end.length) chunks.push(new Int8Array(end));
    return new Blob(chunks, { type: "audio/mpeg" });
  };

  const buildFromSamples = (samples, sr) => {
    setNoteUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    const blob = encodeMp3(samples, sr);
    blobRef.current = blob;
    const url = URL.createObjectURL(blob);
    setNoteUrl(url);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx(); audioCtxRef.current = ctx; srRef.current = ctx.sampleRate;
      const source = ctx.createMediaStreamSource(stream);
      const proc = ctx.createScriptProcessor(4096, 1, 1); procRef.current = proc;
      pcmRef.current = [];
      proc.onaudioprocess = (ev) => { pcmRef.current.push(new Float32Array(ev.inputBuffer.getChannelData(0))); };
      source.connect(proc); proc.connect(ctx.destination);
      setRecording(true); setSecs(0);
      timerRef.current = setInterval(() => setSecs((s) => { if (s + 1 >= 30) { stopRec(); return 30; } return s + 1; }), 1000);
    } catch { toast.error("Microphone access denied."); }
  };

  const stopRec = async () => {
    if (!audioCtxRef.current) return;
    setRecording(false);
    cleanupAudio();
    const total = pcmRef.current.reduce((n, c) => n + c.length, 0);
    if (!total) return;
    const full = new Float32Array(total);
    let off = 0; for (const c of pcmRef.current) { full.set(c, off); off += c.length; }
    samplesRef.current = full;
    const sr = srRef.current;
    const dur = Math.round((full.length / sr) * 10) / 10;
    setEncoding(true);
    try { buildFromSamples(full, sr); setNoteDur(dur); setTrim({ start: 0, end: dur }); }
    finally { setEncoding(false); }
  };

  const applyTrim = () => {
    const sr = srRef.current; const full = samplesRef.current; if (!full) return;
    const a = Math.max(0, Math.floor(trim.start * sr));
    const b = Math.min(full.length, Math.floor(trim.end * sr));
    if (b - a < sr) return toast.error("Voice note must be at least 1 second long.");
    setEncoding(true);
    try { buildFromSamples(full.subarray(a, b), sr); toast.success(`Trimmed to ${Math.round((b - a) / sr * 10) / 10}s`); }
    finally { setEncoding(false); }
  };

  const removeNote = () => {
    setNoteUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    blobRef.current = null; samplesRef.current = null; setNoteDur(0); setTrim({ start: 0, end: 0 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.appt_type.trim()) return toast.error("Please choose an appointment type.");
    setBusy(true);
    try {
      let voiceUrl = "";
      if (blobRef.current) {
        const file = new File([blobRef.current], "voicenote.mp3", { type: "audio/mpeg" });
        voiceUrl = await uploadFile(file, "private");
      }
      await api.post("/appointment-requests", {
        appt_type: form.appt_type,
        preferred_specialist_id: form.preferred_specialist_id || undefined,
        reason: form.reason, image: form.image, voice_note: voiceUrl,
      });
      toast.success("Request submitted — our team will schedule your appointment.");
      setForm({ appt_type: "", preferred_specialist_id: "", reason: "", image: "", voice_note: "" });
      removeNote();
      load();
      onCreated && onCreated();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="patient-requests" className="bg-white border border-line rounded-lg p-5">
      <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-4"><CalendarPlus className="w-5 h-5 text-clinical" /> Request an appointment</h2>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
        <input data-testid="req-type" required placeholder="Appointment type (e.g. General consultation)" value={form.appt_type} onChange={(e) => setForm({ ...form, appt_type: e.target.value })} className={inp} />
        <select data-testid="req-specialist" value={form.preferred_specialist_id} onChange={(e) => setForm({ ...form, preferred_specialist_id: e.target.value })} className={inp}>
          <option value="">Any available clinician</option>
          {specs.map((s) => <option key={s.id} value={s.id}>{s.title ? `${s.title} ` : ""}{s.name}{s.specialty ? ` — ${s.specialty}` : ""}</option>)}
        </select>
        <textarea data-testid="req-reason" rows={2} placeholder="Reason for appointment (optional)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={`${inp} sm:col-span-2 resize-y`} />
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 cursor-pointer hover:bg-clinical/5">
            <ImageIcon className="w-4 h-4" /> {form.image ? "Change image" : "Attach image"}
            <input data-testid="req-image" type="file" accept="image/*" onChange={onImage} className="hidden" />
          </label>
          {form.image && <img src={abs(form.image)} alt="" className="w-10 h-10 rounded object-cover" />}
        </div>
        <div className="sm:col-span-2 flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {!recording ? (
              <button type="button" data-testid="req-record-btn" onClick={startRec} disabled={encoding} className="inline-flex items-center gap-1.5 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 hover:bg-clinical/5 disabled:opacity-60">{encoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />} {encoding ? "Encoding MP3…" : (noteUrl ? "Re-record voice" : "Record 30s voice note")}</button>
            ) : (
              <button type="button" data-testid="req-stop-btn" onClick={stopRec} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-critical rounded-md px-3 py-2 animate-pulse"><Square className="w-4 h-4" /> Stop ({30 - secs}s)</button>
            )}
            {noteUrl && !recording && <audio data-testid="req-voice-preview" src={noteUrl} controls className="h-8" />}
            {noteUrl && !recording && (
              <button type="button" data-testid="req-voice-remove" onClick={removeNote} className="inline-flex items-center gap-1.5 text-sm font-semibold text-critical border border-critical/30 rounded-md px-3 py-2 hover:bg-critical/5"><Square className="w-3.5 h-3.5" /> Remove</button>
            )}
          </div>
          {noteUrl && !recording && (
            <div className="border border-line rounded-md p-3 bg-mist/40" data-testid="req-voice-trim">
              <p className="text-xs font-bold uppercase tracking-wider text-muted2 mb-2">Trim your voice note ({trim.end - trim.start > 0 ? Math.round((trim.end - trim.start) * 10) / 10 : 0}s of {noteDur}s)</p>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-ink">Start: {trim.start}s
                  <input type="range" min={0} max={noteDur} step={0.1} value={trim.start} data-testid="trim-start"
                    onChange={(e) => setTrim((t) => ({ ...t, start: Math.min(Number(e.target.value), t.end - 0.5) }))}
                    className="w-full accent-clinical" />
                </label>
                <label className="text-xs text-ink">End: {trim.end}s
                  <input type="range" min={0} max={noteDur} step={0.1} value={trim.end} data-testid="trim-end"
                    onChange={(e) => setTrim((t) => ({ ...t, end: Math.max(Number(e.target.value), t.start + 0.5) }))}
                    className="w-full accent-clinical" />
                </label>
              </div>
              <button type="button" onClick={applyTrim} disabled={encoding} data-testid="trim-apply" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-1.5 hover:bg-clinical/5 disabled:opacity-60">
                {encoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply trim
              </button>
            </div>
          )}
        </div>
        <button type="submit" disabled={busy} data-testid="req-submit-btn" className="sm:col-span-2 inline-flex items-center justify-center gap-1.5 bg-clinical text-white rounded-md py-2.5 font-semibold hover:bg-clinicaldark disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Submit request
        </button>
      </form>

      {reqs.length > 0 && (
        <div className="mt-5">
          <h3 className="text-xs uppercase tracking-wider font-bold text-muted2 mb-2">My requests</h3>
          <div className="grid gap-2" data-testid="my-requests-list">
            {reqs.map((r) => (
              <div key={r.id} data-testid="my-request-row" className="flex items-center justify-between gap-3 border border-line rounded-md px-3 py-2 text-sm">
                <div className="min-w-0">
                  <span className="text-ink">{r.appt_type}{r.preferred_specialist_name ? ` · ${r.preferred_specialist_name}` : " · Any clinician"}</span>
                  <div className="text-xs text-muted2 mt-0.5 inline-flex items-center gap-1.5" data-testid="my-request-datetime"><Clock className="w-3 h-3" /> {fmtReqDate(r.created_at)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${r.status === "scheduled" ? "bg-calm/10 text-calm" : r.status === "declined" ? "bg-critical/10 text-critical" : "bg-clinical/10 text-clinical"}`}>{r.status}</span>
                  {r.status === "pending" && (
                    <button onClick={() => deleteReq(r)} data-testid="my-request-delete-btn" title="Delete this request" className="text-critical hover:bg-critical/10 rounded p-1"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function NotificationPrefs() {
  const { clinics } = useAppData();
  const [selected, setSelected] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/me/preferences"); setSelected(data.notify_clinics || []); } catch (e) { console.debug("load preferences failed:", e); } })(); }, []);
  const toggle = async (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    setSelected(next);
    try { await api.put("/me/preferences", { notify_clinics: next }); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <section data-testid="notification-prefs" className="bg-white border border-line rounded-lg p-5">
      <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-1"><Bell className="w-5 h-5 text-clinical" /> Notification preferences</h2>
      <p className="text-sm text-muted2 mb-3">Choose which clinic locations you want alerts for. No selection means you receive all.</p>
      <div className="flex flex-wrap gap-2" data-testid="pref-clinics">
        {clinics.map((c) => (
          <button key={c.id} data-testid="pref-clinic-chip" onClick={() => toggle(c.id)} className={`text-sm font-semibold rounded-full px-3 py-1.5 border transition-colors ${selected.includes(c.id) ? "bg-clinical text-white border-clinical" : "border-line text-muted2 hover:border-clinical"}`}>{c.name}</button>
        ))}
        {clinics.length === 0 && <span className="text-sm text-muted2">No clinics configured yet.</span>}
      </div>
    </section>
  );
}

export function PasswordResetPrompt() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  if (!user?.prompt_password_reset) return null;
  const save = async () => {
    if (!curPw) return toast.error("Enter your current password.");
    if (pw.length < 5) return toast.error("Password must be at least 5 characters.");
    if (pw !== pw2) return toast.error("Passwords do not match.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: pw });
      setUser({ ...user, prompt_password_reset: false });
      toast.success("Password updated. Your account is secure.");
      setOpen(false); setCurPw(""); setPw(""); setPw2("");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="password-reset-prompt" className="mb-6 rounded-xl bg-alert/10 border border-alert/30 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-alert shrink-0" />
          <div>
            <p className="font-heading font-bold text-ink">Secure your account</p>
            <p className="text-sm text-muted2">You recently recovered your username. For your security, please set a new password.</p>
          </div>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="prompt-set-password-btn" className="inline-flex items-center gap-1.5 bg-alert text-white text-sm font-semibold rounded-md px-4 py-2">
            Set new password
          </button>
        )}
      </div>
      {open && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <PasswordInput testid="prompt-current-password-input" wrapperClassName="sm:col-span-2" placeholder="Current password" value={curPw} onChange={(e) => setCurPw(e.target.value)} className={inp} />
          <PasswordInput testid="prompt-password-input" placeholder="New password (min 5 characters)" value={pw} onChange={(e) => setPw(e.target.value)} className={inp} />
          <PasswordInput testid="prompt-password2-input" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={inp} />
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm font-semibold text-muted2 hover:text-ink">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="prompt-password-save-btn" className="inline-flex items-center gap-1.5 bg-alert text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save password
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function MyDetailsCard() {
  const { user, setUser } = useAuth();
  const splitName = (user?.name || "").trim().split(/\s+/);
  const [firstName, setFirstName] = useState(user?.first_name || splitName[0] || "");
  const [surname, setSurname] = useState(user?.surname || splitName.slice(1).join(" ") || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingName, setSavingName] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const nameDirty = firstName.trim() !== (user?.first_name || splitName[0] || "") || surname.trim() !== (user?.surname || splitName.slice(1).join(" ") || "");
  const phoneDirty = (phone || "").trim() !== (user?.phone || "");
  const emailDirty = (email || "").trim().toLowerCase() !== (user?.email || "").toLowerCase();

  const saveName = async () => {
    setSavingName(true);
    try {
      const { data } = await api.put("/me/details", { first_name: firstName.trim(), surname: surname.trim() });
      setUser({ ...user, first_name: data.first_name, surname: data.surname, name: data.name });
      toast.success("Name saved");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingName(false); }
  };
  const savePhone = async () => {
    setSavingPhone(true);
    try {
      const { data } = await api.put("/me/phone", { phone: phone.trim() });
      setUser({ ...user, phone: data.phone });
      toast.success("WhatsApp number saved");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingPhone(false); }
  };
  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      const { data } = await api.put("/me/email", { email: email.trim() });
      setUser({ ...user, email: data.email });
      toast.success("Email address saved");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingEmail(false); }
  };

  return (
    <section data-testid="my-details-card" className="bg-white border border-line rounded-lg p-5">
      <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-1"><User className="w-5 h-5 text-clinical" /> My Details</h2>
      <div data-testid="my-details-message" className="text-sm text-muted2 bg-clinical/5 border border-clinical/20 rounded-md px-3 py-2.5 mb-4">
        These details are optional. When provided, they may be used to contact you by email or WhatsApp when necessary — for example appointment reminders or account recovery.
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-2">
        <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-muted2">First name</span>
          <input data-testid="details-first-name" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={`${inp} mt-1.5`} /></label>
        <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-muted2">Surname</span>
          <input data-testid="details-surname" placeholder="Surname" value={surname} onChange={(e) => setSurname(e.target.value)} className={`${inp} mt-1.5`} /></label>
      </div>
      <div className="flex justify-end mb-4">
        <button onClick={saveName} disabled={savingName || !nameDirty} data-testid="details-name-save-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50">
          {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save name
        </button>
      </div>

      <label className="text-xs font-bold uppercase tracking-wider text-muted2 flex items-center gap-1.5 mb-1"><Mail className="w-3.5 h-3.5" /> Email address</label>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input data-testid="contact-email-input" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inp} flex-1 min-w-[220px]`} />
        <button onClick={saveEmail} disabled={savingEmail || !emailDirty} data-testid="contact-email-save-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50">
          {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
        </button>
      </div>

      <label className="text-xs font-bold uppercase tracking-wider text-muted2 flex items-center gap-1.5 mb-1"><Phone className="w-3.5 h-3.5" /> WhatsApp phone number</label>
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="contact-phone-input" type="tel" placeholder="+263 77 123 4567" value={phone} onChange={(e) => setPhone(e.target.value)} className={`${inp} flex-1 min-w-[220px]`} />
        <button onClick={savePhone} disabled={savingPhone || !phoneDirty} data-testid="contact-phone-save-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50">
          {savingPhone ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
        </button>
      </div>
    </section>
  );
}

export function ChangePasswordCard() {
  const { user, setUser } = useAuth();
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const changePassword = async () => {
    if (!curPw) return toast.error("Enter your current password.");
    if (newPw.length < 5) return toast.error("New password must be at least 5 characters.");
    if (newPw !== newPw2) return toast.error("New passwords do not match.");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      setUser({ ...user, prompt_password_reset: false });
      toast.success("Password updated successfully.");
      setCurPw(""); setNewPw(""); setNewPw2("");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingPw(false); }
  };
  return (
    <section data-testid="contact-phone-card" className="bg-white border border-line rounded-lg p-5">
      <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-1"><KeyRound className="w-5 h-5 text-clinical" /> Change Password</h2>
      <div data-testid="recovery-info-message" className="text-sm text-muted2 bg-clinical/5 border border-clinical/20 rounded-md px-3 py-2.5 mb-4">
        Keep your email and WhatsApp number up to date on the My Details page so you can recover your account if you forget your login. Below you can change your password at any time.
      </div>
      <div className="bg-sand/60 border border-line rounded-md px-3 py-2.5 mb-4" data-testid="account-username-box">
        <span className="text-xs font-bold uppercase tracking-wider text-muted2 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Your username</span>
        <p className="font-heading font-bold text-ink mt-0.5" data-testid="account-username">{user?.username || user?.email || "—"}</p>
      </div>
      <div data-testid="change-password-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted2 flex items-center gap-1.5 mb-2"><KeyRound className="w-3.5 h-3.5" /> Set a new password</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <PasswordInput testid="change-current-password" wrapperClassName="sm:col-span-2" placeholder="Current password" value={curPw} onChange={(e) => setCurPw(e.target.value)} className={inp} />
          <PasswordInput testid="change-new-password" placeholder="New password (min 5 characters)" value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inp} />
          <PasswordInput testid="change-new-password2" placeholder="Confirm new password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} className={inp} />
          <div className="sm:col-span-2 flex justify-end">
            <button onClick={changePassword} disabled={savingPw} data-testid="change-password-btn" className="inline-flex items-center gap-1.5 bg-alert text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50">
              {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Update password
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// Back-compat alias (older imports) → the Change Password card.
export const ContactPhoneCard = ChangePasswordCard;


export function PatientMessages() {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const load = useCallback(async () => { try { const { data } = await api.get("/me/messages"); setMsgs(data); } catch (e) { toast.error(apiErr(e)); } }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try { await api.post("/messages", { body }); setBody(""); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const sendVoice = async (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "voice.webm");
    try { await api.post("/messages/voice", fd, { headers: { "Content-Type": "multipart/form-data" } }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <section data-testid="patient-messages" className="bg-white border border-line rounded-lg p-5">
      <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-4"><MessageSquare className="w-5 h-5 text-clinical" /> Inbox</h2>
      <div className="max-h-72 overflow-y-auto flex flex-col gap-2 mb-3" data-testid="patient-message-thread">
        {msgs.length === 0 && <p className="text-sm text-muted2">No messages yet. Send a private message to our team.</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.from_staff ? "bg-sand self-start" : "bg-clinical text-white self-end"}`}>
            <div className="text-[10px] uppercase font-bold opacity-70 mb-0.5">{m.from_name}</div>
            {m.audio_url ? (
              <audio controls src={`${process.env.REACT_APP_BACKEND_URL}${m.audio_url}`} className="mt-1 w-full h-9" data-testid="patient-message-voice" />
            ) : m.body}
            {m.created_at && <div className="text-[10px] opacity-60 mt-1 text-right" data-testid="patient-message-time">{fmtStamp(m.created_at)}</div>}
            {m.from_staff && !m.audio_url && <div className="mt-1"><TranslateButton text={m.body} testid={`msg-translate-${m.id}`} /></div>}
          </div>
        ))}
      </div>
      <form onSubmit={send} className="flex gap-2">
        <input data-testid="patient-message-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message…" className={inp} />
        <button type="submit" data-testid="patient-message-send" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 font-semibold"><Send className="w-4 h-4" /></button>
      </form>
      <div className="mt-2">
        <MessageVoiceRecorder testid="patient-msg-voice" onSend={sendVoice} />
      </div>
    </section>
  );
}

export function PatientTraining() {
  const [events, setEvents] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/public-training");
      setEvents(data);
      const upd = await registerTrainingViews(data);
      if (Object.keys(upd).length) setEvents((prev) => prev.map((e) => (upd[e.id] != null ? { ...e, views: upd[e.id] } : e)));
    } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <section data-testid="patient-training" className="bg-white border border-line rounded-lg p-5">
      <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-4"><GraduationCap className="w-5 h-5 text-clinical" /> Learn &amp; Training</h2>
      <div className="grid gap-4" data-testid="patient-training-list">
        {events.length === 0 && <p className="text-sm text-muted2">No training or health-education materials yet. Check back soon.</p>}
        {events.map((t) => (
          <div key={t.id} data-testid="patient-training-row" className="border border-line rounded-md p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="font-heading font-bold text-ink">{t.title} {t.date && <span className="text-xs text-muted2 font-normal">· {t.date}</span>}</div>
              <span data-testid="patient-training-views" className="inline-flex items-center gap-1 text-xs text-muted2 shrink-0"><Eye className="w-3.5 h-3.5" /> {t.views || 0}</span>
            </div>
            {t.created_at && <div className="text-xs text-muted2 mt-0.5 inline-flex items-center gap-1.5" data-testid="patient-training-time"><Clock className="w-3 h-3" /> Posted {fmtStamp(t.created_at)}</div>}
            {t.description && <p className="text-sm text-muted2 mt-1">{t.description}</p>}
            {t.video_url && <VideoEmbed url={t.video_url} testid="patient-training-video" />}
            {t.materials?.map((m, i) => <a key={i} href={abs(m.url)} target="_blank" rel="noreferrer" className="text-sm text-clinical underline block mt-1">📎 {m.name}</a>)}
            <TrainingRsvp trainingId={t.id} counts={t.rsvp} testid="patient-training-rsvp" />
          </div>
        ))}
      </div>
    </section>
  );
}

export function PatientGroupCalls() {
  const [calls, setCalls] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/group-calls");
      setCalls(data.filter((c) => c.starts_at));
    } catch (e) { console.debug("group calls unavailable:", e); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (calls.length === 0) return null;
  return (
    <section data-testid="patient-group-calls" className="bg-white border border-line rounded-lg p-5">
      <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-4"><Video className="w-5 h-5 text-clinical" /> Upcoming group calls</h2>
      <div className="grid gap-4" data-testid="patient-group-calls-list">
        {calls.map((c) => (
          <div key={c.id} data-testid="patient-group-call-row" className="border border-line rounded-md p-4">
            <div className="font-heading font-bold text-ink">{c.title}</div>
            <p className="text-sm text-muted2 mt-0.5">{new Date(c.starts_at).toLocaleString()}</p>
            <TrainingRsvp trainingId={c.id} counts={c.rsvp} endpoint={`/group-calls/${c.id}/rsvp`} testid="patient-group-call-rsvp" />
          </div>
        ))}
      </div>
    </section>
  );
}

